import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { SOPCardId, SOPTabId } from './sop-card-utils';

interface SOPCardStateValue {
  isExpanded: boolean;
  isFullView: boolean;
  toggleCompressed: () => void;
  setCompressed: (compressed: boolean) => void;
  activeTab: SOPTabId;
  setActiveTab: (tab: SOPTabId) => void;
  isCardCollapsed: (id: SOPCardId) => boolean;
  toggleCardCollapsed: (id: SOPCardId) => void;
}

const SOPCardStateContext = createContext<SOPCardStateValue | undefined>(
  undefined
);

interface ProviderProps {
  children: ReactNode;
  initialTab?: SOPTabId;
}

export function SOPCardStateProvider({ children, initialTab }: ProviderProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFullView, setIsFullView] = useState(false);
  const [activeTab, setActiveTabState] = useState<SOPTabId>(
    initialTab ?? SOPTabId.SITUATIONS
  );
  const [collapseOverrides, setCollapseOverrides] = useState<
    Partial<Record<SOPCardId, boolean>>
  >({});

  const toggleCompressed = useCallback(() => {
    setCollapseOverrides({});
    if (isExpanded) {
      setIsFullView(false);
      setIsExpanded(false);
      return;
    }
    setIsFullView(true);
    setIsExpanded(true);
  }, [isExpanded]);

  const setCompressed = useCallback((compressed: boolean) => {
    setCollapseOverrides({});
    setIsFullView(false);
    setIsExpanded(!compressed);
  }, []);

  const setActiveTab = useCallback((tab: SOPTabId) => {
    setActiveTabState(tab);
    setIsFullView(false);
    setIsExpanded(true);
  }, []);

  const isCardCollapsed = useCallback(
    (id: SOPCardId) => collapseOverrides[id] ?? false,
    [collapseOverrides]
  );

  const toggleCardCollapsed = useCallback(
    (id: SOPCardId) =>
      setCollapseOverrides((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );

  const value = useMemo(
    () => ({
      isExpanded,
      isFullView,
      toggleCompressed,
      setCompressed,
      activeTab,
      setActiveTab,
      isCardCollapsed,
      toggleCardCollapsed,
    }),
    [
      isExpanded,
      isFullView,
      toggleCompressed,
      setCompressed,
      activeTab,
      setActiveTab,
      isCardCollapsed,
      toggleCardCollapsed,
    ]
  );

  return (
    <SOPCardStateContext.Provider value={value}>
      {children}
    </SOPCardStateContext.Provider>
  );
}

export function useSOPCardState() {
  const context = useContext(SOPCardStateContext);
  if (!context) {
    throw new Error(
      'useSOPCardState must be used within a SOPCardStateProvider'
    );
  }
  return context;
}
